// src/lib/marketData.js
// ─────────────────────────────────────────────────────────────
// Central market data service with circuit breaker pattern.
// All components should use getPrice() — never fetch Yahoo directly.
//
// Fallback chain:
//   Stocks/ETFs/Commodities → Yahoo Finance → Cached (DB)
//   Crypto                  → CoinCap → CoinGecko → Cached (DB)

import { supabase } from './supabaseClient'
import { authFetch } from './authFetch'

const CIRCUIT_BREAKER_THRESHOLD = 3      // failures before opening
const CIRCUIT_BREAKER_TIMEOUT   = 300000 // 5 min before retry (ms)

// ── In-memory circuit breaker state ───────────────────────
const cb = {
  yahoo_finance: { failures: 0, openedAt: null },
  coincap:       { failures: 0, openedAt: null },
}

function isCircuitOpen(source) {
  const s = cb[source]
  if (!s || !s.openedAt) return false
  if (Date.now() - s.openedAt > CIRCUIT_BREAKER_TIMEOUT) {
    s.failures = 0
    s.openedAt = null
    return false
  }
  return true
}

function recordFailure(source) {
  const s = cb[source]
  if (!s) return
  s.failures++
  if (s.failures >= CIRCUIT_BREAKER_THRESHOLD) {
    s.openedAt = Date.now()
    _updateHealthDB(source, 'down')
  } else {
    _updateHealthDB(source, 'degraded')
  }
}

function recordSuccess(source) {
  if (!cb[source]) return
  cb[source] = { failures: 0, openedAt: null }
  _updateHealthDB(source, 'healthy')
}

async function _updateHealthDB(source, status) {
  if (!supabase) return
  const now = new Date().toISOString()
  await supabase.from('data_source_health').upsert({
    id: source,
    status,
    ...(status === 'healthy'
      ? { last_success: now, failure_count: 0 }
      : { last_failure: now }
    ),
    updated_at: now,
  }, { onConflict: 'id' })
}

// ── Yahoo Finance fetch (via /api/proxy) ───────────────────
async function fetchFromYahoo(ticker) {
  if (isCircuitOpen('yahoo_finance')) {
    throw new Error('Circuit breaker open: yahoo_finance')
  }

  const t   = ticker === 'BRK' ? 'BRK-B' : ticker
  const url = `https://query1.finance.yahoo.com/v8/finance/chart/${t}?interval=1d&range=1d&includePrePost=false`

  let res
  try {
    // Try proxy first (required in production)
    const proxyRes = await authFetch(`/api/proxy?url=${encodeURIComponent(url)}`, {
      signal: AbortSignal.timeout(8000),
    })
    if (proxyRes.ok) {
      res = proxyRes
    } else if ([401, 403, 429].includes(proxyRes.status)) {
      // Fall through to direct
      res = await fetch(url, {
        headers: { 'User-Agent': 'Mozilla/5.0', Accept: 'application/json' },
        signal: AbortSignal.timeout(8000),
      })
    } else {
      res = proxyRes
    }
  } catch {
    // Proxy unreachable — try direct
    res = await fetch(url, {
      headers: { 'User-Agent': 'Mozilla/5.0', Accept: 'application/json' },
      signal: AbortSignal.timeout(8000),
    })
  }

  if (!res.ok) {
    recordFailure('yahoo_finance')
    throw new Error(`Yahoo Finance HTTP ${res.status}`)
  }

  const json   = await res.json()
  const result = json?.chart?.result?.[0]
  if (!result) {
    recordFailure('yahoo_finance')
    throw new Error(json?.chart?.error?.description ?? 'No data from Yahoo')
  }

  const meta      = result.meta ?? {}
  const adj       = result.indicators?.adjclose?.[0]?.adjclose ?? []
  const q         = result.indicators?.quote?.[0] ?? {}
  const price     = meta.regularMarketPrice ?? adj[adj.length - 1] ?? 0
  const prev      = meta.chartPreviousClose ?? meta.previousClose ?? price
  const change    = price - prev
  const changePct = prev ? (change / prev) * 100 : 0

  recordSuccess('yahoo_finance')

  return {
    ticker,
    price:     +price.toFixed(2),
    change:    +change.toFixed(2),
    changePct: +changePct.toFixed(2),
    currency:  meta.currency ?? 'USD',
    name:      meta.longName ?? meta.shortName ?? ticker,
    source:    'yahoo_finance',
    fetched_at: new Date().toISOString(),
    stale:     false,
  }
}

// ── CoinCap fetch ──────────────────────────────────────────
async function fetchFromCoinCap(ticker) {
  if (isCircuitOpen('coincap')) {
    throw new Error('Circuit breaker open: coincap')
  }

  const res = await fetch(
    `https://api.coincap.io/v2/assets?search=${ticker}&limit=1`,
    { signal: AbortSignal.timeout(8000) }
  )

  if (!res.ok) {
    recordFailure('coincap')
    throw new Error(`CoinCap HTTP ${res.status}`)
  }

  const data  = await res.json()
  const asset = data.data?.[0]
  if (!asset) {
    recordFailure('coincap')
    throw new Error('CoinCap: no asset found')
  }

  recordSuccess('coincap')

  return {
    ticker,
    price:     parseFloat(asset.priceUsd),
    change:    parseFloat(asset.changePercent24Hr ?? 0),
    changePct: parseFloat(asset.changePercent24Hr ?? 0),
    currency:  'USD',
    name:      asset.name ?? ticker,
    source:    'coincap',
    fetched_at: new Date().toISOString(),
    stale:     false,
  }
}

// ── CoinGecko free fallback ────────────────────────────────
async function fetchFromCoinGecko(ticker) {
  const id  = ticker.toLowerCase()
  const res = await fetch(
    `https://api.coingecko.com/api/v3/simple/price?ids=${id}&vs_currencies=usd&include_24hr_change=true`,
    { signal: AbortSignal.timeout(8000) }
  )
  if (!res.ok) throw new Error(`CoinGecko HTTP ${res.status}`)
  const d = await res.json()
  const entry = d[id]
  if (!entry) throw new Error('CoinGecko: no data')
  return {
    ticker,
    price:     entry.usd,
    change:    entry.usd_24h_change ?? 0,
    changePct: entry.usd_24h_change ?? 0,
    currency:  'USD',
    name:      ticker,
    source:    'coingecko',
    fetched_at: new Date().toISOString(),
    stale:     false,
  }
}

// ── Cached DB fallback ─────────────────────────────────────
async function fetchFromCache(ticker) {
  if (!supabase) {
    return { ticker, price: null, currency: 'USD', source: 'cache', fetched_at: null, stale: true }
  }

  const userId = (await supabase.auth.getUser()).data.user?.id
  if (!userId) {
    return { ticker, price: null, currency: 'USD', source: 'cache', fetched_at: null, stale: true }
  }

  const { data } = await supabase
    .from('portfolio_snapshots')
    .select('value, snapshot_at')
    .eq('user_id', userId)
    .order('snapshot_at', { ascending: false })
    .limit(1)
    .single()

  return {
    ticker,
    price:     data?.value ?? null,
    currency:  'USD',
    source:    'cache',
    fetched_at: data?.snapshot_at ?? null,
    stale:     true,
  }
}

// ── Main export ────────────────────────────────────────────
export async function getPrice(ticker, assetType = 'stock') {
  if (assetType === 'crypto') {
    try { return await fetchFromCoinCap(ticker) } catch { /* fall through */ }
    try { return await fetchFromCoinGecko(ticker) } catch { /* fall through */ }
    return fetchFromCache(ticker)
  }

  try { return await fetchFromYahoo(ticker) } catch { /* fall through */ }
  return fetchFromCache(ticker)
}

// ── Data source status ─────────────────────────────────────
export async function getDataSourceStatus() {
  if (!supabase) {
    // Return in-memory state when Supabase unavailable
    return Object.entries(cb).map(([id, state]) => ({
      id,
      status:   state.openedAt ? 'down' : state.failures > 0 ? 'degraded' : 'healthy',
      updated_at: new Date().toISOString(),
    }))
  }

  const { data } = await supabase.from('data_source_health').select('*')
  return data ?? []
}

// ── Expose circuit breaker state directly (for UI) ─────────
export function getCircuitBreakerState() {
  return Object.entries(cb).reduce((acc, [id, state]) => {
    acc[id] = {
      open:      !!state.openedAt,
      failures:  state.failures,
      openedAt:  state.openedAt,
    }
    return acc
  }, {})
}
